import React, { useCallback, useEffect, useMemo, memo, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import B2BLayout from '../../../layouts/b2b'
import {
  getMeasurementFormData,
  getMeasurementId,
  getMeasurementFormError,
  getMeasurementFormLoading,
  getVisibleFormData,
  getMeasurementParentId,
} from './selectors'
import { DefaultJsonFormContextProps } from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import { getMeasurementEnums, getMeasurementForm } from '../../../store/measurement/action-creators'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import MeasurementTabs from './ui/tabs/measurement-tabs'
import { MeasurementFormsHeader } from './ui/measurement-forms/measurement-form-header/measurement-form-header'
import { useSaveResult } from './hooks/useSaveResult'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import { Result, Skeleton } from 'antd'

function AdminNewObjectPage() {
  const { id } = useParams<{ id: string }>()
  const parentId = useSelector(getMeasurementParentId)
  const pageTitle = useMemo(() => (parentId ? `Замер №${parentId}` : ''), [parentId])
  const breadcrumbs = useMemo(
    () => [
      {
        href: '/measurements',
        title: 'Замеры',
      },
      {
        title: pageTitle,
      },
    ],
    [pageTitle],
  )

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const appealId = useSelector(getMeasurementId)
  const entityType = 'measurement'
  const appealsFormIsLoading = useSelector(getMeasurementFormLoading)
  const appealsFormError = useSelector(getMeasurementFormError)
  const appealsFormData = useSelector(getMeasurementFormData)
  const measurementFormData = useSelector(getMeasurementFormData)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useSaveResult()
  const [enumsRequested, setEnumsRequested] = useState(false)
  const [isStageTransitioning, setIsStageTransitioning] = useState(false)
  const stageTransitionLockRef = useRef(false)

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }),
    [id],
  )

  useEffect(() => {
    let mounted = true
    if (mounted) {
      setEnumsRequested(false)
      dispatch(getMeasurementForm(id)).then((result: unknown) => {
        if (mounted && typeof result === 'string') {
          navigate(result)
        }
      })
    }
    return () => {
      mounted = false
    }
  }, [id, dispatch, navigate])

  // Второй запрос - если форма загрузилась успешно, запрашиваем енумы
  useEffect(() => {
    if (appealsFormData && !appealsFormError && !appealsFormIsLoading && !enumsRequested) {
      setEnumsRequested(true)
      if (id) {
        dispatch(getMeasurementEnums(id))
      }
    }
  }, [appealsFormData, appealsFormError, appealsFormIsLoading, enumsRequested, id, dispatch])

  const runStageTransition = useCallback(
    async (is_done?: boolean) => {
      if (stageTransitionLockRef.current) {
        return
      }
      stageTransitionLockRef.current = true
      setIsStageTransitioning(true)
      try {
        await saveResult(
          id,
          visibleFormData,
          measurementFormData?.stages?.current,
          true,
          is_done,
          measurementFormData?.stages,
        )
      } finally {
        stageTransitionLockRef.current = false
        setIsStageTransitioning(false)
      }
    },
    [id, saveResult, visibleFormData, measurementFormData],
  )

  const handleToOrder = useCallback(async () => {
    await runStageTransition(undefined)
  }, [runStageTransition])

  const handleToFinish = useCallback(async () => {
    await runStageTransition(true)
  }, [runStageTransition])

  useEffect(() => {
    if (id === 'new' && appealId) {
      navigate({
        pathname: `/appeal/${appealId}`,
      })
    }
  }, [id, appealId])

  if (!id) return null

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      {appealsFormIsLoading && <Skeleton active />}
      {!appealsFormIsLoading && appealsFormError && (
        <Result
          status={typeof appealsFormError === 'object' ? appealsFormError.statusCode : 'error'}
          title="Ошибка"
          subTitle={
            typeof appealsFormError === 'object' ? appealsFormError.message : appealsFormError
          }
        />
      )}
      {!appealsFormIsLoading && !appealsFormError && appealsFormData && (
        <>
          <DefaultJsonFormContext.Provider value={defaultProps}>
            <MeasurementFormsHeader
              id={id}
              isFinished={
                measurementFormData?.stages &&
                measurementFormData?.stages?.current ===
                  Object.keys(measurementFormData?.stages?.items).length - 1
              }
              stage={measurementFormData?.stages?.current}
              toOrder={handleToOrder}
              toFinish={handleToFinish}
              showTitle={false}
              isStageTransitioning={isStageTransitioning}
            />
            <MeasurementTabs id={id} />
          </DefaultJsonFormContext.Provider>
        </>
      )}
    </B2BLayout>
  )
}

export default memo(AdminNewObjectPage)
